import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useDateFilter } from '../../dateFilter'
import { useGroupFilter } from '../../groupFilter'
import { IApiDataType } from '../types'

const query = gql`
  query ListFarwestFlashKpis(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    listLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationGroupId
        metricData
        metricSummaryData
      }
    }

    listLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        metricData
        metricSummaryData
      }
    }

    listLocationMarketAndOpenDate(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        openedAt
        market
        ro
      }
    }
  }
`

export const farwestDailyOpsConfigs = {
  salesForecast: 'price',
  salesActual: 'price',
  salesVariance: 'price',
  laborScheduledHours: 'number',
  laborActualHours: 'number',
  laborVarianceHours: 'number',
  otScheduledHours: 'number',
  otActualHours: 'number',
  laborScheduledPercent: 'percent',
  laborActualPercent: 'percent',
  laborVariancePercent: 'percent',
  cashDeposit: 'price',
  cashOs: 'price',
  totalCards: 'price',
  paidIn: 'price',
  paidOut: 'price',
  checkAverage: 'price',
  refund: 'price',
  voidSales: 'price',
  comps: 'price',
  promos: 'price',
  netSales: 'price',
  splh: 'price',
  tplh: 'number',
  classicCaseCountPerDay: 'number',
  tendersCaseCountPerDay: 'number',
  bonelessCaseCountPerDay: 'number',
} as const

const KPIS = [
  'sales_forecast',
  'sales_actual',
  'sales_variance',
  'labor_scheduled_hours',
  'labor_actual_hours',
  'labor_variance_hours',
  'ot_scheduled_hours',
  'ot_actual_hours',
  'labor_scheduled_percent',
  'labor_actual_percent',
  'labor_variance_percent',
  'cash_deposit',
  'cash_os',
  'total_cards',
  'paid_in',
  'paid_out',
  'check_average',
  'refund',
  'void_sales',
  'comps',
  'promos',
  'net_sales',
  'splh',
  'tplh',
  'classic_case_count_per_day',
  'tenders_case_count_per_day',
  'boneless_case_count_per_day',
]

const useFarwestDailyOps = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: hasGroupBy
          ? groupFilter?.ids
          : groupFilter?.list?.map((g) => g.id),
        intersected_location_group_ids: groupFilter?.intersectedIds,
        metrics: KPIS,
      },
      hasGroupBy,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const customizedData: any =
        data?.[
          hasGroupBy
            ? 'listLocationMetricValues'
            : 'listLocationGroupMetricValues'
        ]?.nodes
      const locationDetails: any = data?.listLocationMarketAndOpenDate?.nodes

      if (!customizedData) return null

      const source = customizedData.map((locationData: any) => {
        const groupInfo = groupFilter?.list?.find(
          (l) =>
            l.id ===
            (hasGroupBy
              ? locationData.locationId
              : locationData.locationGroupId),
        )
        const marketDetail = locationDetails?.find(
          (l: any) => l.locationId === locationData.locationId,
        )
        const kpisData: { [key: string]: number | null } = {}

        for (const key in locationData.metricData) {
          if (locationData.metricData.hasOwnProperty(key)) {
            const newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())

            // why: tplh's metric def declares DOLLAR unit but the value is a
            // transactions-per-labor-hour ratio, so the cents conversion must
            // be skipped or it displays 100x too large
            // sync: src/pared/Routes/renderer/table/hooks/useFarwestTrendDailySplh.ts
            if (newKey === 'tplh') {
              kpisData[newKey] = locationData.metricData[key].value
              continue
            }

            switch (locationData.metricData[key].unit) {
              case 'DOLLAR':
              case 'PERCENTAGE':
                kpisData[newKey] =
                  locationData.metricData[key].value === null
                    ? null
                    : locationData.metricData[key].value * 100
                break

              default:
                kpisData[newKey] = locationData.metricData[key].value
                break
            }
          }
        }

        return {
          ...kpisData,
          openAt: marketDetail?.openedAt,
          market: marketDetail?.market,
          ro: marketDetail?.ro,
          groupInfo,
        }
      })

      const [rawSummary] = customizedData?.filter(
        (locationData: any) => locationData.metricSummaryData != null,
      )
      const summary: { [key: string]: number | null } = {}
      for (const key in rawSummary?.metricSummaryData) {
        if (rawSummary.metricSummaryData.hasOwnProperty(key)) {
          const newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
          const metricUnit = rawSummary.metricSummaryData[key].unit

          // why: tplh's metric def declares DOLLAR unit but the value is a
          // transactions-per-labor-hour ratio, so the cents conversion must
          // be skipped or it displays 100x too large
          // sync: src/pared/Routes/renderer/table/hooks/useFarwestTrendDailySplh.ts
          if (newKey === 'tplh') {
            summary[newKey] = rawSummary.metricSummaryData[key].value
            continue
          }

          switch (metricUnit) {
            case 'DOLLAR':
            case 'PERCENTAGE':
              summary[newKey] =
                rawSummary.metricSummaryData[key].value === null
                  ? null
                  : rawSummary.metricSummaryData[key].value * 100
              break

            default:
              summary[newKey] = rawSummary.metricSummaryData[key].value
              break
          }
        }
      }

      return {
        source,
        summary: {
          ...summary,
          openAt: '',
          market: '',
        },
      }
    }, [groupFilter, data, hasGroupBy]),
    loading,
  }
}

export default useFarwestDailyOps
